import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  Bug, Lightbulb, Search, Clock, CheckCircle, AlertCircle, 
  MessageSquare, ChevronUp, ChevronDown, Eye, Send, Loader2,
  ThumbsUp, Map, Sparkles, Calendar
} from 'lucide-react';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  triaged: { label: 'Triaged', color: 'bg-purple-100 text-purple-700', icon: Eye },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  testing: { label: 'Testing', color: 'bg-orange-100 text-orange-700', icon: Sparkles },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  wont_fix: { label: "Won't Fix", color: 'bg-slate-100 text-slate-700', icon: AlertCircle },
  duplicate: { label: 'Duplicate', color: 'bg-slate-100 text-slate-700', icon: AlertCircle },
};

const TYPE_CONFIG = {
  bug: { label: 'Bug', color: 'bg-red-100 text-red-700', icon: Bug },
  feature: { label: 'Feature', color: 'bg-blue-100 text-blue-700', icon: Lightbulb },
  improvement: { label: 'Improvement', color: 'bg-green-100 text-green-700', icon: Sparkles },
};

export default function HelpPortal() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [roadmap, setRoadmap] = useState({ in_progress: [], coming_soon: [], recently_completed: [] });
  const [activeTab, setActiveTab] = useState('my-tickets');

  useEffect(() => {
    loadTickets();
    loadRoadmap();
  }, [statusFilter, typeFilter]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      
      const response = await api.feedback.getMyTickets(params);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error('Failed to load your tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadRoadmap = async () => {
    try {
      const response = await api.feedback.getRoadmap();
      setRoadmap(response.data);
    } catch (error) {
      console.error('Failed to load roadmap:', error);
    }
  };

  const handleViewTicket = async (ticket) => {
    try {
      const response = await api.feedback.getTicket(ticket.id);
      setSelectedTicket(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error('Failed to load ticket details:', error);
      toast.error('Failed to load ticket details');
    }
  };

  const handleVote = async (ticketId, hasVoted) => {
    try {
      if (hasVoted) {
        await api.feedback.unvote(ticketId);
        toast.success('Vote removed');
      } else {
        await api.feedback.vote(ticketId);
        toast.success('Vote recorded!');
      }
      loadRoadmap();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update vote');
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.title?.toLowerCase().includes(query) ||
        ticket.description?.toLowerCase().includes(query) ||
        ticket.ticket_number?.toString().includes(query)
      );
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="help-portal">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Help Portal</h1>
            <p className="text-slate-500">Track your bug reports and feature requests</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
            <TabsTrigger value="my-tickets" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              My Tickets
            </TabsTrigger>
            <TabsTrigger value="roadmap" className="gap-2">
              <Map className="h-4 w-4" />
              Roadmap
            </TabsTrigger>
          </TabsList>

          {/* My Tickets Tab */}
          <TabsContent value="my-tickets" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="bug">Bugs</SelectItem>
                  <SelectItem value="feature">Features</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tickets List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No tickets found</h3>
                  <p className="text-slate-500">
                    {tickets.length === 0 
                      ? "You haven't submitted any bug reports or feature requests yet."
                      : "No tickets match your current filters."
                    }
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredTickets.map((ticket) => {
                  const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || AlertCircle;
                  const TypeIcon = TYPE_CONFIG[ticket.type]?.icon || Bug;
                  
                  return (
                    <Card 
                      key={ticket.id} 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleViewTicket(ticket)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-mono text-sm text-slate-500">#{ticket.ticket_number}</span>
                              <Badge className={TYPE_CONFIG[ticket.type]?.color || 'bg-slate-100'}>
                                <TypeIcon className="h-3 w-3 mr-1" />
                                {TYPE_CONFIG[ticket.type]?.label || ticket.type}
                              </Badge>
                              <Badge className={STATUS_CONFIG[ticket.status]?.color || 'bg-slate-100'}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {STATUS_CONFIG[ticket.status]?.label || ticket.status}
                              </Badge>
                            </div>
                            <h3 className="font-medium text-slate-900 mb-1 truncate">{ticket.title}</h3>
                            <p className="text-sm text-slate-500 line-clamp-2">{ticket.description}</p>
                          </div>
                          <div className="text-right text-sm text-slate-500 flex-shrink-0">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(ticket.created_at).toLocaleDateString()}
                            </div>
                            {ticket.type === 'feature' && (
                              <div className="flex items-center gap-1 mt-1 text-blue-600">
                                <ThumbsUp className="h-3 w-3" />
                                {ticket.vote_count} votes
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Roadmap Tab */}
          <TabsContent value="roadmap" className="space-y-6">
            {/* In Progress */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-500" />
                In Progress
              </h2>
              {roadmap.in_progress.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-slate-500">
                    No features currently in progress
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {roadmap.in_progress.map((item) => (
                    <RoadmapCard 
                      key={item.id} 
                      item={item} 
                      onVote={handleVote}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Coming Soon */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-500" />
                Coming Soon
              </h2>
              {roadmap.coming_soon.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-slate-500">
                    No features planned yet. Submit a feature request!
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {roadmap.coming_soon.map((item) => (
                    <RoadmapCard 
                      key={item.id} 
                      item={item} 
                      onVote={handleVote}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Recently Completed */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Recently Completed
              </h2>
              {roadmap.recently_completed.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-slate-500">
                    No recently completed features
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {roadmap.recently_completed.map((item) => (
                    <RoadmapCard 
                      key={item.id} 
                      item={item} 
                      showVote={false}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Ticket Detail Dialog */}
        <TicketDetailDialog
          open={detailDialogOpen}
          onOpenChange={setDetailDialogOpen}
          ticket={selectedTicket}
          onUpdate={loadTickets}
        />
      </div>
    </DashboardLayout>
  );
}

function RoadmapCard({ item, onVote, showVote = true }) {
  const [voting, setVoting] = useState(false);

  const handleVoteClick = async (e) => {
    e.stopPropagation();
    if (!onVote) return;
    
    setVoting(true);
    try {
      await onVote(item.id, item.user_voted);
    } finally {
      setVoting(false);
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={STATUS_CONFIG[item.status]?.color || 'bg-slate-100'}>
                {STATUS_CONFIG[item.status]?.label || item.status}
              </Badge>
              {item.category && (
                <Badge variant="outline" className="text-xs">
                  {item.category.replace('_', '/')}
                </Badge>
              )}
            </div>
            <h3 className="font-medium text-slate-900 mb-1">{item.title}</h3>
            <p className="text-sm text-slate-500 line-clamp-2">{item.description}</p>
          </div>
          {showVote && (
            <Button
              variant={item.user_voted ? "default" : "outline"}
              size="sm"
              className={`flex-shrink-0 ${item.user_voted ? 'bg-blue-600' : ''}`}
              onClick={handleVoteClick}
              disabled={voting}
            >
              {voting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <ThumbsUp className={`h-4 w-4 mr-1 ${item.user_voted ? 'fill-current' : ''}`} />
                  {item.vote_count}
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TicketDetailDialog({ open, onOpenChange, ticket, onUpdate }) {
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!ticket) return null;

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await api.feedback.addComment(ticket.ticket.id, { content: newComment });
      toast.success('Comment added');
      setNewComment('');
      onUpdate?.();
      // Reload ticket details
      const response = await api.feedback.getTicket(ticket.ticket.id);
      ticket.comments = response.data.comments;
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const ticketData = ticket.ticket;
  const StatusIcon = STATUS_CONFIG[ticketData.status]?.icon || AlertCircle;
  const TypeIcon = TYPE_CONFIG[ticketData.type]?.icon || Bug;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-slate-500">#{ticketData.ticket_number}</span>
            <Badge className={TYPE_CONFIG[ticketData.type]?.color}>
              <TypeIcon className="h-3 w-3 mr-1" />
              {TYPE_CONFIG[ticketData.type]?.label}
            </Badge>
            <Badge className={STATUS_CONFIG[ticketData.status]?.color}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {STATUS_CONFIG[ticketData.status]?.label}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{ticketData.title}</h2>
            <p className="text-sm text-slate-500 mt-1">
              Submitted on {new Date(ticketData.created_at).toLocaleString()}
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
            <p className="text-slate-600 whitespace-pre-wrap">{ticketData.description}</p>
          </div>

          {ticketData.type === 'bug' && (
            <>
              {ticketData.steps_to_reproduce && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Steps to Reproduce</h4>
                  <p className="text-slate-600 whitespace-pre-wrap">{ticketData.steps_to_reproduce}</p>
                </div>
              )}
              {(ticketData.expected_behavior || ticketData.actual_behavior) && (
                <div className="grid grid-cols-2 gap-4">
                  {ticketData.expected_behavior && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-green-700 mb-2">Expected</h4>
                      <p className="text-green-600 text-sm">{ticketData.expected_behavior}</p>
                    </div>
                  )}
                  {ticketData.actual_behavior && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-red-700 mb-2">Actual</h4>
                      <p className="text-red-600 text-sm">{ticketData.actual_behavior}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {ticketData.type === 'feature' && ticketData.use_case && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-700 mb-2">Use Case</h4>
              <p className="text-blue-600">{ticketData.use_case}</p>
            </div>
          )}

          {ticketData.resolution_notes && (
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h4 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Resolution
              </h4>
              <p className="text-green-600">{ticketData.resolution_notes}</p>
            </div>
          )}

          {/* Comments */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Comments ({ticket.comments?.length || 0})
            </h4>
            
            {ticket.comments?.length > 0 && (
              <div className="space-y-3 mb-4">
                {ticket.comments.map((comment) => (
                  <div key={comment.id} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-sm text-slate-900">{comment.author_name}</span>
                      <Badge variant="outline" className="text-xs">{comment.author_role}</Badge>
                      <span className="text-xs text-slate-500">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600">{comment.content}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Add Comment */}
            <div className="flex gap-2">
              <Textarea
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={2}
                className="flex-1"
              />
              <Button onClick={handleAddComment} disabled={submitting || !newComment.trim()}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
